/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and
 * international treaties. Unauthorized use, reproduction, distribution, or
 * modification of any content from Knowledge for Decisions (K4D) is strictly
 * prohibited. For inquiries regarding the use or licensing of K4D's
 * copyrighted materials, please contact us at contact@k4d.com.br. "Knowledge
 * for Decisions (K4D)" and the slogan "Using data to make precise decisions"
 * are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import { SKELETON_BLOCK } from '@/shared/ui/components/basic/data-state/skeletons/skeleton-primitives';

/**
 * Skeleton placeholder matching the ClientDetailView layout:
 * breadcrumb + header card (avatar + name + tabs) followed by a
 * two-column grid (tab content + sidebar score cards).
 */
export const ClientDetailSkeleton: React.FC = () => (
  <div
    data-testid="client-detail-skeleton"
    className="flex-1 min-h-0 overflow-y-auto bg-[var(--surface-primary)] p-[var(--space-5)]"
  >
    <div className={`${SKELETON_BLOCK} h-4 w-48 mb-[var(--space-4)]`} />

    <div className="rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] shadow-sm overflow-hidden mb-[var(--space-4)]">
      <div className="flex items-center justify-between gap-[var(--space-4)] px-[var(--space-5)] py-[var(--space-4)]">
        <div className="flex items-center gap-[var(--space-4)]">
          <div className={`${SKELETON_BLOCK} h-14 w-14 rounded-full`} />
          <div className="flex flex-col gap-[var(--space-2)]">
            <div className={`${SKELETON_BLOCK} h-5 w-40`} />
            <div className={`${SKELETON_BLOCK} h-3 w-24`} />
          </div>
        </div>
        <div className="flex gap-[var(--space-2)]">
          <div className={`${SKELETON_BLOCK} h-9 w-20`} />
          <div className={`${SKELETON_BLOCK} h-9 w-24`} />
        </div>
      </div>

      <div className="flex gap-[var(--space-1)] px-[var(--space-5)] pb-[var(--space-3)]">
        {[80, 70, 120, 80, 80].map((w, i) => (
          <div key={i} className={`${SKELETON_BLOCK} h-8`} style={{ width: w }} />
        ))}
      </div>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-[1fr_340px] gap-[var(--space-4)]">
      <div className="flex flex-col gap-[var(--space-4)]">
        <div className={`${SKELETON_BLOCK} h-5 w-32`} />
        <div className={`${SKELETON_BLOCK} h-4 w-full`} />
        <div className={`${SKELETON_BLOCK} h-4 w-5/6`} />
        <div className={`${SKELETON_BLOCK} h-4 w-3/4`} />
      </div>

      <div className="flex flex-col gap-[var(--space-4)]">
        {[3, 3, 2].map((rows, ci) => (
          <div
            key={ci}
            className="rounded-(--radius-lg) border border-[var(--border-subtle)] bg-[var(--surface-primary)] p-[var(--space-4)] flex flex-col gap-[var(--space-3)]"
          >
            <div className={`${SKELETON_BLOCK} h-4 w-24`} />
            {Array.from({ length: rows }).map((_, i) => (
              <div key={i} className="flex justify-between gap-[var(--space-2)]">
                <div className={`${SKELETON_BLOCK} h-3 w-24`} />
                <div className={`${SKELETON_BLOCK} h-3 w-16`} />
              </div>
            ))}
          </div>
        ))}
      </div>
    </div>
  </div>
);
